/**
 * Dragging things into folders, outside a board: rows and cards are dragged (a selected one takes the selection with
 * it, rows inside an open folder too), folders' rows and cards, a folder page's breadcrumb and the list itself (the
 * level it shows: out of the folders open in it) take them; never a folder dragged, nor what's under it, nor where
 * it all came from. Items move in one bulk change; folders one by
 * one (not into themselves, nor where they are; one of their name there is said, and it stays).
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { BulkResult, FolderOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { type FolderIndex, isFolderId, isWithin, useFolderIndex } from "../../lib/folders.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError, bump } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";

/** What's dragged: its ids (a board's columns read the first, as `text/plain`). */
const MIME = "application/x-revisa-ids";

/** What's being dragged in this page, and the folder it's all from (undefined: not one, or not known). */
let dragging: { ids: readonly string[]; from: string | null | undefined } | null = null;
// Dropped or not, it's over.
globalThis.addEventListener?.(
  "dragend",
  () => {
    dragging = null;
  },
  true,
);

/** Starts dragging `ids` (things of the space: items, folders), all from folder `from` (null: the root) if known. */
export function dragIds(e: DragEvent, ids: readonly string[], from?: string | null): void {
  if (!e.dataTransfer || ids.length === 0) {
    return;
  }
  dragging = { ids, from };
  e.dataTransfer.setData(MIME, JSON.stringify(ids));
  e.dataTransfer.setData("text/plain", ids[0] ?? "");
  e.dataTransfer.effectAllowed = "move";
}

/** What was dropped: the ids dragged here, or one (from elsewhere in the app). */
function droppedIds(e: DragEvent): string[] {
  const raw = e.dataTransfer?.getData(MIME);
  if (raw) {
    try {
      const ids: unknown = JSON.parse(raw);
      return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  }
  const one = e.dataTransfer?.getData("text/plain") ?? "";
  return one ? [one] : [];
}

/** Whether what's dragged is ours (not a file from the desktop, a link from another page). */
const ours = (e: DragEvent) => e.dataTransfer?.types.includes(MIME) ?? false;

/** Whether what's dragged here can go into `to`: not where it already is, nor a folder into itself or under it. */
function takes(index: FolderIndex | undefined, to: string | null): boolean {
  if (!dragging) {
    return true;
  }
  if (dragging.from !== undefined && dragging.from === to) {
    return false;
  }
  return !dragging.ids.some((id) => isFolderId(id) && (id === to || (to !== null && isWithin(index, to, id))));
}

/** Moves `ids` into folder `to.id` (null: the root), named `to.name`. */
async function moveInto(
  space: Pick<SpaceOut, "id">,
  index: FolderIndex | undefined,
  into: { id: string | null; name: string },
  ids: string[],
) {
  const { id: to, name } = into;
  const items = ids.filter((id) => !isFolderId(id));
  // Not into itself, nor into what's under it.
  const folders = ids.filter((id) => isFolderId(id) && id !== to && !(to !== null && isWithin(index, to, id)));
  let moved = 0;
  if (items.length > 0) {
    const r = await api.post<BulkResult>(spaceApi(space, "/items/bulk"), { ids: items, set: { folder: to } });
    moved += r.matched;
  }
  for (const id of folders) {
    const f = await api.get<FolderOut>(spaceApi(space, `/folders/${id}`));
    if ((f.parent ?? null) === to) {
      continue;
    }
    try {
      await api.patch<FolderOut>(spaceApi(space, `/folders/${id}`), { parent: to, ver: f.ver });
      moved += 1;
    } catch (e) {
      if (asApiError(e).code === "name_taken") {
        toast(t("folders.taken", { name: f.name }), "error");
        continue;
      }
      throw e;
    }
  }
  if (moved > 0) {
    toast(t("folders.movedTo", { name }));
    bump();
  }
}

/**
 * A place things can be dropped into: folder `to` (null: the root), named `name`. Its handlers for what it's drawn
 * with, and whether something's over it; `off`: not a target (nothing to change, or not allowed).
 */
export function useDropInto(space: Pick<SpaceOut, "id">, to: string | null, name: string, off = false) {
  const index = useFolderIndex(space);
  const [over, setOver] = useState(false);
  if (off) {
    return { over: false, props: {} };
  }
  return {
    over,
    props: {
      onDragOver: (e: DragEvent) => {
        // Not taken here: left to what's around it (the list's level, say).
        if (ours(e) && takes(index.data, to)) {
          e.preventDefault();
          e.stopPropagation();
          setOver(true);
        }
      },
      onDragLeave: () => setOver(false),
      onDrop: (e: DragEvent) => {
        if (!(ours(e) && takes(index.data, to))) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        moveInto(space, index.data, { id: to, name }, droppedIds(e)).catch((err: unknown) =>
          toast(asApiError(err).message, "error"),
        );
      },
    },
  };
}

/** A drop target's look while something's over it. */
export const DROP_OVER = "bg-blue-50 ring-2 ring-inset ring-blue-300";
/** The list's level's, around the whole of it, against its border (outside it: what's in the list draws over its
 *  inside). */
export const DROP_AROUND = "outline-2 outline-offset-0 outline-blue-300 outline-solid";
